<template>
  <div class="shop-set-delivery-address">
    <div class="wrapper">
      <div class="subtitle" style="text-align: left; padding: 16px">
        <div v-if="error" @click="locationInput = ''">
          <i class="error-hint has-text-info">{{ error }}</i>
        </div>
        <div v-else>
          <span @click="close">
            <i class="fa fa-chevron-left" />&nbsp;
            <span v-if="isAgent">{{
              $t("delivery.set-customer-address")
            }}</span>
            <span v-else>{{ $t("delivery.set-your-address") }}</span>
          </span>
          <span v-if="showSavedPlaces" class="button is-pulled-right places-btn" @click="showPastAddresses">
            {{ $t("delivery.saved-places") }}
          </span>
          <span v-else-if="loadingAddresses" class="button is-pulled-right places-btn">
            <i class="fa fa-spinner fa-spin" />
          </span>
          <span v-else-if="isAgent" class="button is-pulled-right places-btn" @click="triggerPaste">
            <a class="has-text-link"><i class="far fa-edit" />&nbsp;lat,lon</a>
          </span>
        </div>
      </div>
      <div class="field" style="margin-bottom: 0px">
        <p class="control has-icons-left">
          <input ref="deliveryAddressAutocomplete" class="input is-medium" v-model="locationInput"
            :placeholder="placeholderText" onfocus="value = ''" onpaste="return false;" ondrop="return false;"
            autocomplete="off" v-on:keyup="checkLength" type="text" />
          <span class="icon is-small is-left">
            <i class="fa fa-globe-asia" />
          </span>
        </p>
      </div>

      <div v-if="loadingAddresses">
        <b-skeleton height="20px"></b-skeleton>
        <b-skeleton height="50px"></b-skeleton>
        <b-skeleton height="50px"></b-skeleton>
      </div>

      <div v-show="!loadingAddresses" class="map-wrapper">
        <div :id="mapID" class="map">
          <div class="leaflet-bottom leaflet-right">
            <div v-if="userCoords" class="center-btn button is-white is-rounded is-small" @click="recenter">
              <i class="fa fa-crosshairs fa-lg" :class="{ 'fa-spin': spin }" />
            </div>
          </div>
          <div class="leaflet-top">
            <div class="dropoff-warning" style="pointer-events: initial">
              <div class="notification" style="font-size: 1.1em">
                <div v-if="usingSavedAddress" class="has-text-black">
                  <p style="color: #999; font-weight: 450">
                    <span style="display: inline-block; margin-top: 3px">Saved Address:</span>
                    <span class="tag is-pulled-right" @click="openGoogleMaps">{{ mapMarkerCoords }}&nbsp;<i
                        class="fa fa-external-link-alt fa-sm has-text-link" /></span>
                  </p>
                  <!-- <div @click="setAddressNotes"> -->
                  <div>
                    <p style="margin-top: 4px">
                      <b>{{ locationInput }}</b>
                    </p>
                    <p v-if="fullAddress">
                      {{ fullAddress }}
                    </p>

                    <div @click="editingAddressNotes = true">
                      <p v-if="notes" class="stop-name notes has-text-info">
                        <i class="far fa-comment has-text-grey"></i>&nbsp;{{
                          notes
                        }}
                      </p>
                      <p v-else class="stop-name notes has-text-grey">
                        <i class="fa fa-pencil-alt fa-sm has-text-info" />Set
                        address notes / driver instructions
                      </p>
                    </div>
                  </div>
                </div>
                <div v-else class="has-text-link">
                  <i>
                    Start typing to look up address or move map pin to location
                  </i>
                </div>
              </div>

              <div v-if="userDistance > 40" class="tag is-danger">
                <i style="font-size: 1.2em">
                  <i class="fa fa-map-marker-alt" />
                  {{ prettyUserDistance }}
                  {{ $t(" from your current location") }}
                </i>
              </div>
            </div>

            <div v-if="showYourLocations" class="stops-wrapper no-scrollbar">
              <div class="list">
                <p class="previously-used">
                  {{ $t("delivery.previously-used-addresses") }}
                </p>
                <div v-for="(l, i) in yourLocations" :key="i" class="list-item is-pointer"
                  @click="chooseFrequentLocation(l)" v-ripple>
                  <p class="stop-name no-scrollbar" style="font-size: 1em; color: #333">
                    <i class="fa fa-history fa-sm" />
                    {{ l.name || "Pin Location - Please enter details" }}
                  </p>
                  <p class="stop-name no-scrollbar">
                    {{ l.address }}
                  </p>
                  <p v-if="l.notes" class="stop-name notes no-scrollbar has-text-info">
                    <i class="far fa-comment has-text-grey" />&nbsp;{{
                      l.notes
                    }}
                  </p>
                </div>
                <div class="list-item is-pointer" style="background-color: whitesmoke; padding: 16px"
                  @click="triggerEnterNewLocation">
                  <p class="stop-name no-scrollbar" style="font-size: 1em">
                    <i class="fa fa-pencil-alt fa-sm" />
                    {{ $t("delivery.or-enter-a-new-location") }}
                  </p>
                </div>
              </div>
              <div style="padding: 20px 40px; max-width: 700px" @click="triggerChooseOnMap">
                <div class="map-btn">
                  {{ $t("or") }}<br />
                  {{ $t("ride.choose-on-map") }}<br />
                  <i class="fa fa-map-marker-alt has-text-link" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!showYourLocations" class="confirm-wrapper">
        <div v-if="usingSavedAddress" class="field is-grouped" style='margin-bottom:0'>
          <p class="control is-expanded">
            <span class="button is-medium is-fullwidth is-success" @click="preConfirmLocation">{{
              $t("delivery.use-saved-address") }}</span>
          </p>
          <p class="control">
            <span class="button is-medium is-dark" @click="editSavedAddress">{{
              $t("edit")
            }}</span>
          </p>
        </div>
        <div v-else-if="loadingAddresses" class="button is-medium has-text-grey">
          Loading Addresses...
        </div>
        <div v-else class="button is-medium is-success is-fullwidth" @click="preConfirmLocation">
          <span v-if="isAgent">{{ $t("confirm-customer-address") }}</span>
          <span v-else>{{ $t("confirm-your-address") }}</span>
        </div>
        <div class="cover-leaflet-links" @click="showAttribution" />
      </div>

      <div v-if="editingAddressNotes" class="edit-notes-wrapper" @click="saveAddressNotes" />
      <div v-if="editingAddressNotes" class="edit-notes-panel">
        <div @click="saveAddressNotes">
          <button class="delete is-medium is-pulled-right" />
          <p class="subtitle has-text-dark">Editing Address Notes</p>
          <p class="has-text-link" style="margin: 6px 0">
            Is there anything the driver should know about the delivery
            location? Unit #, house #, building name etc.
          </p>
        </div>
        <p style="margin-bottom: 8px">
          <textarea class="textarea" v-model="notes" placeholder="Unit #, house #, building name etc."></textarea>
        </p>
        <div class="field is-grouped" style="margin-bottom: 10px">
          <p class="control">
            <span class="button is-medium" @click="notes = null"><i class="far fa-trash-alt" /></span>
          </p>
          <p class="control is-expanded">
            <span class="button is-medium is-fullwidth is-link" @click="saveAddressNotes">Save Address Notes</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $Scriptjs from "scriptjs";
import L from "leaflet";
import "leaflet.locatecontrol";
// import "leaflet-edgebuffer";
import "leaflet.awesome-markers";
import geo from "@/geo";

// var pickupIcon = { icon: "street-view", markerColor: "blue" };
// var pickupDragIcon = { icon: "crosshairs", markerColor: "blue", spin: true };
var iconOpts = { icon: "flag-checkered", markerColor: "green" };
var iconDragOpts = { icon: "crosshairs", markerColor: "green", spin: true };

const picanteLocations = [
  {
    address: "R. Rodrigues de Faria 103, Lisbon, Portugal",
    lat: 38.704000260246644,
    lon: -9.178937373011506,
    name: "LX Factory - Solana Conf",
  },
  {
    address: "Parque Eduardo VII, Lisbon, Portugal",
    lat: 38.72889567369671,
    lon: -9.155309073011505,
    name: "Estufa Fria - Solana Conf",
  },
  {
    address: "MAAT, Pavilhão Poente Av. Brasília, Lisbon, Portugal",
    lat: 38.69699698589075,
    lon: -9.191755103693513,
    name: "SUD Lisboa - Solana Conf",
  },
];
export default {
  name: "ShopSetDeliveryAddress",
  props: {
    business: Object, // "th", "sg" << for google places autocomplete
    isAgent: Object, // using as Boolean, but passing in Object
  },
  data() {
    return {
      mapID: "shop-set-delivery-address-map",
      editingAddressNotes: false,
      usingSavedAddress: false,
      zoomControl: null,
      map: undefined,
      marker: undefined,
      error: null,
      locationInput: null,
      place: undefined, // google place result
      addressName: "", // address.name
      fullAddress: "", // address.address
      notes: null, // address.notes
      streetName: "",
      googleStreetName: "",
      spin: false,
      userDistance: 0,
      moved: false,
      checkedGPS: false,
      savedAddresses: [],
      loadingAddresses: false,
      z: 0,
    };
  },
  watch: {
    showYourLocations(bool) {
      this.toggleZoomControl(!bool);
    },
  },
  computed: {
    placeholderText() {
      return this.$t("enter-location");
      //   if (this.isAgent) return this.$t("Enter Location");
      //   if (this.userCoords) return this.$t("passenger.current-location");
      //   else return this.$t("enter-location");
    },
    country() {
      return this.business.country;
    },
    businessCoords() {
      return [this.business.lat, this.business.lon];
    },
    userCoords() {
      return this.$store.getters.getUserLatLon; // getters.getUserLocation
    },
    prettyUserDistance() {
      return geo.prettyDistance(this.userDistance, 9999);
    },
    mapMarkerCoords() {
      // for visual and google maps url
      if (!this.marker) return null;
      return (
        this.marker.getLatLng().lat.toFixed(6) +
        "," +
        this.marker.getLatLng().lng.toFixed(6)
      );
    },
    yourLocations() {
      if (this.savedAddresses && this.savedAddresses.length) {
        return this.savedAddresses.filter((a) => a.name); // filter blank string names
      }
      var arr = this.$store.state.userStops.filter((s) => s.name); // filter blank string names
      if (arr.length < 4) return arr;
      return arr.slice(0, 4); // return [ { name: "My House", lat: 13.729986, lon: 100.574575 } ];
    },
    showYourLocations() {
      if (!this.yourLocations) return false;
      if (!this.yourLocations.length) return false;
      if (this.isAgent) return false;
      if (this.locationInput) return false;
      if (this.moved) return false;
      return true;
    },
    showSavedPlaces() {
      return (
        !this.showYourLocations &&
        this.yourLocations.length &&
        !this.isAgent &&
        !this.loadingAddresses
      );
    },
  },
  mounted() {
    var url = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBYOwhqakcxLQQLpljhkug2WsNSA02cqss&libraries=places`;
    $Scriptjs(url, () => this.initGoogle());
    // setTimeout(() => this.initMap(), 1000); // delay to allow map component to exist
    this.initMap();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    openGoogleMaps() {
      var url = `http://www.google.com/maps/place/${this.mapMarkerCoords}`;
      window.open(url, "_target");
    },
    showAttribution() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'>Map Attribution Info</p>
<p>LeafletJS <a>https://leafletjs.com</a></p>
<p>OpenStreetMap <a>https://openstreetmap.org</a></p>
        </div>`,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Use Address",
        cancelButtonText: "Back to Map",
        cancelButtonColor: this.bulmaColor('info'),
        confirmButtonColor: this.bulmaColor('success')
      }).then(val => {
        if (val.value) this.preConfirmLocation();
      })
    },
    //  copied from editDeliveryNotes() {
    setAddressNotes() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'>
        <i class='fa fa-shipping-fast fa-sm'></i>&nbsp;
        Set Delivery Notes
        </p>
        <p style='margin-bottom:10px'>Is there anything the driver should know about the delivery location?</p>
        <p><i style='color:grey'>Unit #, house #, building name etc.</i><p>
        </div>`,
        input: "textarea",
        inputValue: this.notes || "",
        showCloseButton: true,
        confirmButtonText: this.$t("save"),
        inputValidator: (txt) => {
          this.notes = txt;
        },
      }).then((val) => {
        if (val.value) setTimeout(() => this.pleaseCheckMapLocation(), 100);
      });
    },
    saveAddressNotes() {
      this.editingAddressNotes = false;
      this.pleaseCheckMapLocation();
    },
    pleaseCheckMapLocation() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'>
        <i class='fa fa-map-marker-alt has-text-danger'></i>&nbsp;Double Check Location on Map 🙏🏼</p>
        <p style='margin-bottom:10px'>Please ensure the coordinates on the map are correct otherwise drivers may have difficulty finding the address.</p>
        <p style='margin-bottom:10px'>If the map pin is off by more than 1,000 meters the delivery fee may change.</p>
        <p>To move the pin, click "Edit" in bottom right of map.</p>
        </div>`,
      });
    },
    toggleZoomControl(show) {
      // var opt = { position: "topright" };
      var opt = { position: "bottomleft" };
      if (show) this.zoomControl = L.control.zoom(opt).addTo(this.map);
      else if (this.zoomControl) this.map.removeControl(this.zoomControl);
    },
    triggerPaste() {
      //  <p style='font-size:0.8em; opacity:0.6'>
      //   https://www.google.com.sg/maps/search/199%2F72+Moo+Baan+Nai+Fun+2,+Soi+5a,+Canal+Road,+Mae+Hia/@18.739951,98.9316323,14.54z
      //   </p>
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'>
        <i class='fa fa-map-marker-alt'></i>&nbsp;Enter address coordinates
        </p>
        <p style='margin-bottom:12px'>
        You can manually enter the address coordinates if you know them.
        </p>
         <p style='margin-bottom:12px'>
        Enter as <code>lat,lon</code>
        </p>
        <p style='margin-bottom: 8px'>
        You can find the  coordinates on google maps by right clicking on the location. 
        </p>
       
        </div>`,
        input: "text",
        inputPlaceholder: "18.739951,98.9316323",
        confirmButtonText: this.$t("submit"),
        cancelButtonText: this.$t("cancel"),
        showCancelButton: true,
        showCloseButton: true,
        // cancelButtonColor: this.bulmaColor("danger"),
        confirmButtonColor: this.bulmaColor("info"),
        inputValidator: (txt) => {
          if (!txt || txt.length < 6 || txt.indexOf(",") === -1)
            return "Enter latitude,longitude";
        },
      }).then((val) => {
        if (val.value) {
          var nospace = val.value.replace(/ /g, "");
          var latString = nospace.split(",")[0];
          var lonString = nospace.split(",")[1];
          var lat = parseFloat(latString);
          var lon = parseFloat(lonString);
          var msg = `Incorrect format [ ${val.value} ] please enter latitude,longitude with no spaces.`;
          if (!lat || !lon) return this.errorToast(msg);
          this.infoToast(lat + ", " + lon);
          this.updateMarkerLocation([lat, lon], true);
        }
      });
    },
    // removeMap() {
    //   if (this.map != undefined || this.map != null) {
    //     this.map.off();
    //     this.map.remove();
    //   }
    // },
    getAddresses() {
      this.loadingAddresses = true;
      // var loader = this.loadingDots();
      this.$store
        .dispatch("getUserAddresses")
        .then((res) => {
          console.log("getUserAddresses", res.data);
          if (res.data) {
            this.savedAddresses = res.data;
            setTimeout(() => this.showPastAddresses(), 300);
          } else {
            this.$refs["deliveryAddressAutocomplete"].focus();
          }
        })
        .catch((err) => console.log("getUserAddresses error", err))
        .finally(() => {
          setTimeout(() => {
            // loader.hide();
            this.loadingAddresses = false;
          }, 300);
          if (this.isPicante()) {
            this.addPicanteAddresses();
            setTimeout(() => this.showPastAddresses(), 500);
          }
        });
    },
    addPicanteAddresses() {
      this.savedAddresses = this.savedAddresses.concat(picanteLocations);
      console.log("savedAddresses", this.savedAddresses);
    },
    showPastAddresses() {
      // this.map.fire("click"); // trying to stop flyTo zoom/pan that blocks Saved Places...
      this.locationInput = null;
      this.moved = false;
      this.z += 1;
    },
    chooseFrequentLocation(l) {
      this.locationInput = l.name;
      this.notes = l.notes;
      this.addressName = l.name;
      this.fullAddress = l.address;
      this.marker.closePopup();
      this.updateMarkerLocation([l.lat, l.lon]);
      // freeze map so user can't accidentally move pin
      this.usingSavedAddress = true;
      this.disableMapMovement();
      this.triggerSavedAddressPopup();
    },
    editSavedAddress() {
      this.usingSavedAddress = false;
      this.enableMapMovement();
      this.triggerEditAddressPopup();
    },
    triggerSavedAddressPopup() {
      var opts = { closeButton: false, autoPan: false };
      var text = "Deliver to Saved Address";
      var content = `<i style='font-weight:500; opacity:0.8; color:black'>${text}</i>`; // <a onclick="clickConfirmPickup()">
      this.marker.bindPopup(content, opts);
      setTimeout(() => this.marker.openPopup(), 300);
    },
    triggerEditAddressPopup() {
      var opts = { closeButton: false, autoPan: false };
      // var text = "Edit Delivery Location";
      var text = this.$t("delivery.deliver-here-?");
      var content = `<i style='font-weight:500; opacity:0.8; color:grey'>${text}</i>`; // <a onclick="clickConfirmPickup()">
      this.marker.bindPopup(content, opts);
      setTimeout(() => this.marker.openPopup(), 300);
    },
    disableMapMovement() {
      this.map.dragging.disable();
      this.map.touchZoom.disable();
      this.map.doubleClickZoom.disable();
      this.map.scrollWheelZoom.disable();
      this.map.boxZoom.disable();
      this.map.keyboard.disable();
    },
    enableMapMovement() {
      this.map.dragging.enable();
      this.map.touchZoom.enable();
      this.map.doubleClickZoom.enable();
      this.map.scrollWheelZoom.enable();
      this.map.boxZoom.enable();
      this.map.keyboard.enable();
    },
    checkLength() {
      var errMsg = `Select address from list or move pin on map`;
      if (this.locationInput && this.locationInput.length > 15)
        this.error = errMsg;
      else this.error = null;
    },
    initGoogle() {
      console.log("initGoogle");
      var opts = {
        types: [],
        componentRestrictions: { country: this.country },
      };
      var el = this.$refs["deliveryAddressAutocomplete"];
      var autocomplete = new google.maps.places.Autocomplete(el, opts);

      var coords = {
        lat: this.businessCoords[0],
        lng: this.businessCoords[1],
      };
      this.biasAutocomplete(autocomplete, coords); // bias to business location
      this.geolocate(autocomplete); // then bias to user gps coords if possible

      autocomplete.addListener("place_changed", () => {
        this.editSavedAddress(); // just means not using saved address...
        this.error = "";
        var place = autocomplete.getPlace();
        console.log("google place", place);
        this.addressName = place.name;
        this.fullAddress = place.formatted_address;
        this.notes = null; // clear this
        this.url = place.url;
        this.place = place;
        if (place.address_components) {
          if (place.address_components[0]) {
            this.googleStreetName = place.address_components[0].long_name;
          }
        }

        this.locationInput = place.name;
        var lat = place.geometry.location.lat();
        var lng = place.geometry.location.lng();
        this.marker.closePopup();
        this.updateMarkerLocation([lat, lng]);
      });
    },
    // Bias autocomplete to the user geolocation,
    geolocate(autocomplete) {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition((position) => {
        var coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.biasAutocomplete(autocomplete, coords);
      });
    },
    biasAutocomplete(autocomplete, coords) {
      var circle = new google.maps.Circle({
        center: coords,
        radius: 1, //position.coords.accuracy,
      });
      autocomplete.setBounds(circle.getBounds());
    },
    triggerChooseOnMap() {
      this.usingSavedAddress = false;
      this.moved = true; // to close saved locations
      this.enableMapMovement(); // re-enable map movement in case it was disabled
    },
    triggerEnterNewLocation() {
      this.usingSavedAddress = false;
      this.moved = true; // to close saved locations
      setTimeout(() => {
        this.$refs["deliveryAddressAutocomplete"].focus();
      }, 300);
    },
    recenterBusiness() {
      this.map.setView(this.businessCoords, 17);
    },
    // called by parent, ShopPromosTab
    reset() {
      this.moved = false;
      this.checkedGPS = false;
      this.recenter();
      this.getAddresses();
      // this.$refs["deliveryAddressAutocomplete"].focus();
    },
    recenter() {
      this.editSavedAddress();
      this.locationInput = null;
      this.map.invalidateSize();
      if (!this.userCoords) return this.recenterBusiness();
      this.updateMarkerLocation(this.userCoords, true);
      this.spin = true;
      setTimeout(() => (this.spin = false), 600);
    },
    updateMarkerLocation(coords, flyTo) {
      this.marker.setLatLng(coords);
      if (flyTo) this.map.panTo(coords);
      else this.map.setView(coords, 17);
    },
    initMap() {
      var tilesURL = this.$t("geo.map-tiles"); // language specific maps
      var initialLocation = this.userCoords;
      if (!initialLocation) initialLocation = this.businessCoords;

      // this.removeMap(); // could this fix the errors?
      this.map = L.map(this.mapID, {
        attributionControl: true,
        zoomControl: false,
      });
      this.map.setView(initialLocation, 17);
      L.tileLayer(tilesURL, {
        // edgeBufferTiles: 1,
        tileSize: 512,
        zoomOffset: -1,
        minZoom: 12,
        maxZoom: 19,
        attribution: `&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>`,
      }).addTo(this.map);
      this.map.invalidateSize();

      // L.control.zoom({ position: "topright" }).addTo(this.map);

      //   var iconOpts = this.isPickup ? pickupIcon : dropoffIcon;
      //   var iconDragOpts = this.isPickup ? pickupDragIcon : dropoffDragIcon;
      var icon = L.AwesomeMarkers.icon(iconOpts);
      var iconDrag = L.AwesomeMarkers.icon(iconDragOpts);

      this.marker = L.marker(initialLocation, { icon: icon }).addTo(this.map);
      this.marker.normalIcon = icon;
      this.marker.dragIcon = iconDrag;

      // set popup default
      var opts = { closeButton: false, autoPan: false };
      var text = this.$t("delivery.deliver-here-?");
      var content = `<i style='font-weight:500; opacity:0.8; color:grey'>${text}</i>`; // <a onclick="clickConfirmPickup()">
      this.marker.bindPopup(content, opts);
      //   if (this.isPickup) {
      setTimeout(() => this.marker.openPopup(), 1000);

      this.map.on("move", () => {
        this.marker.setLatLng(this.map.getCenter());
        this.moved = true;
      });

      this.map.on("dragstart", () => {
        this.marker.closePopup();
        this.marker.setIcon(this.marker.dragIcon);
      });

      this.map.on("moveend", () => {
        this.marker.setLatLng(this.map.getCenter()); // needed?
        this.marker.center = this.map.getCenter();
        this.marker.setIcon(this.marker.normalIcon);
        this.marker.openPopup();
        this.checkAddressFarAway();
      });

      if (this.userCoords) this.drawUserLocation();
    },
    checkAddressFarAway() {
      if (!this.userCoords) return false;
      var distMeters = geo.distance(this.marker.center, this.userCoords);
      this.userDistance = distMeters;
    },
    drawUserLocation() {
      var icon = L.divIcon({
        html: `<div class="blue-dot pulse"></div>`,
        popupAnchor: [1, -6],
        className: "none",
      });
      this.userMarker = L.marker(this.userCoords, { icon: icon });
      this.userMarker.addTo(this.map);
      var popupOpts = { closeButton: false, autoPan: true };
      this.userMarker.bindPopup(this.$t("ride.your-current-gps"), popupOpts);
    },
    isDefaultLocation() {
      // if (this.moved) return false; // looks real if they moved pin
      console.log("geos", geo.geos());
      // check BKK and CM default locations in geos.js, or better DB
      var defaultLocations = geo.geos().filter((g) => g.name === "Chiang Mai");
      var defaultLoc = null;
      var coords = this.marker.getLatLng(); // this.userCoords
      defaultLocations.forEach((g) => {
        var dist = geo.distanceToUserInMeters(g, coords);
        if (dist < 20) {
          defaultLoc = g;
          defaultLoc.distance = dist;
        }
      });
      if (!defaultLoc) return false;
      console.log("default location", defaultLoc);
      // otherwise it's a default location
      var msg = `Search for your address<br>or move the map pin 📍<br>to set your delivery location.`;
      this.successToast(msg, "bottom", 1000 * 9);
      let info = `${this.$store.getters.getNameAndNumber} @ ${this.business.name}`;
      var notification = `🧟‍♀️ Customer tried to use default coords: ${info}`;
      this.sendSuperuserNotification(notification);
      return true;
    },
    preConfirmLocation() {
      if (!this.moved && !this.checkedGPS && !this.userCoords) {
        this.$swal({
          html: `<div style='text-align:left'>
          <p style='font-size:1.2em; margin-bottom:10px'>
          <i class='fa fa-map-marker-alt'></i>&nbsp;Double check GPS
          </p>
          <p>Sometimes device GPS can be incorrect. Please check the exact location before confirming 🙏🏼</p>
          </div>`,
          showCloseButton: true,
          showCancelButton: true,
          cancelButtonText: "Check GPS",
          confirmButtonText: "Confirm",
        }).then((val) => {
          if (val.value) this.confirmLocation();
          else this.checkedGPS = true;
        });
        return;
      }
      // check it's not a default location
      // ...
      if (this.isDefaultLocation()) return;
      this.confirmLocation();
    },
    confirmLocation() {
      var errMsg = "Set dropoff location to confirm";
      if (this.disableButton) return (this.error = errMsg);
      var coords = this.marker.getLatLng();
      console.log("confirmLocation", coords);
      this.emitLocation(coords);
      // var loader = this.loadingDots(this.bulmaColor("success"));
      // this.$store
      //   .dispatch("reverseGeocode", coords)
      //   .then((res) => {
      //     this.console("reverseGeocode", res);
      //     this.streetName = res.data.address.road;
      //     if (!this.addressName) this.locationInput = this.streetName;
      //     this.emitLocation(coords);
      //   })
      //   .catch((err) => {
      //     console.error("reverseGeocode error", err);
      //     this.$store.dispatch("logError", {
      //       type: "set_delivery_address_reverse_geocode",
      //       error: err.toString(),
      //       coords: coords,
      //     });
      //     this.sendDebugEmail(coords, err);
      //     this.emitLocation(coords); // don't block if reverseGeocode returns error
      //   })
      //   .finally(() => loader.hide());
      // this.sendDebugEmail(coords, null);
    },
    sendDebugEmail(coords, err) {
      var obj = {
        type: "set_delivery_address_reverse_geocode_err",
        user_id: this.$store.state.user.id,
        uuid: this.$store.getters.getUUID,
        mobile: this.$store.getters.phoneNumber,
        name: this.$store.getters.realName,
        app: process.env.VUE_APP_TITLE,
        coords: JSON.stringify(coords),
        err: err ? err.ToString() : "UNKNOWN ERROR",
      };
      this.$store.dispatch("sendEmail", obj).catch((err) => console.error(err));
    },
    emitLocation(coords) {
      if (!this.streetName) this.streetName = this.googleStreetName;
      // if (!this.addressName && !this.streetName) // old
      // if (!this.addressName) return this.requireAddressName(coords);
      return this.reallyEmit(coords);
    },
    requireAddressName(coords) {
      this.$swal({
        html: `<div style='text-align:left'>
          <p style='font-size:1.2em; margin-bottom:10px'>
          <i class='fa fa-map-marker-alt'></i>&nbsp;Enter Address or Street
          </p>
          <p>Please provide a building name or street name and number so the delivery doesn't get lost 🙏🏼</p>
          </div>`,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Confirm",
        input: "text",
        inputPlaceholder: "Address or building name",
        autocomplete: "none",
        inputAttributes: {
          autocomplete: "none", // off or none...
        },
        inputValidator: (val) => {
          if (!val || val.length < 4) return `Valid input is required`;
        },
      }).then((val) => {
        if (val.value) {
          this.addressName = val.value;
          this.reallyEmit(coords);
        }
      });
    },
    reallyEmit(coords) {
      var locationData = {
        // mapID: this.mapID, stop_type: this.stopType,
        name: this.parseStopName(),
        address: this.fullAddress,
        notes: this.notes,
        lat: coords.lat,
        lon: coords.lng,
      };
      this.$emit("setLocation", locationData); // also closes component
      this.close();
    },
    dedupeName(str) {
      var arr = str.split(", ");
      var uniqueArr = arr.filter((item, pos) => {
        return arr.indexOf(item) == pos;
      });
      return uniqueArr.join(", ");
    },
    parseStopName() {
      if (!this.addressName && this.streetName) return this.streetName;
      if (this.addressName && !this.streetName) return this.addressName;
      var combined = `${this.streetName}, ${this.addressName}`; // otherwise use both since exists
      return this.dedupeName(combined);
    },
  },
};
</script>

<style scoped>
.wrapper {
  position: absolute;
  top: 0;
  /* height: 100vh; */
  height: 100%;
  width: 100vw;
  max-width: 700px;
  overflow: hidden;
  background-color: #aaa;
  z-index: 21;
}

.map-wrapper {
  position: relative;
  height: calc(100% - 100px);
  width: 100%;
  max-width: 700px;
}

.map {
  position: relative;
  height: 100%;
  width: 100%;
  max-width: 700px;
  border: 1px solid lightgrey;
  z-index: 2;
}

.input {
  border-radius: 0px;
}

/* input::-webkit-input-placeholder {
  color: #2693da !important;
  opacity: 0.8;
} */
.subtitle {
  color: white;
  /* color: grey; */
  font-weight: 450;
  margin-bottom: 0;
}

.delete {
  position: absolute;
  right: 12px;
  top: 12px;
}

.confirm-wrapper {
  position: fixed;
  /* bottom: 16px; */
  bottom: 0;
  padding: 12px;
  padding-bottom: 32px;
  z-index: 1200;
  width: 100vw;
  max-width: 700px;
  pointer-events: auto;
}

.confirm-wrapper .button {
  /* box-shadow: 1px 1px 1px #777, 1px 1px 3px white; */
  box-shadow: 0 0 2px 1px white, 0 0 4px black;
}

.center-btn {
  margin-right: 9px;
  /* margin-top: 75px; */
  margin-bottom: 100px;
  /* padding: 0 5px; */
  padding: 3px 6px;
  pointer-events: auto;
  box-shadow: 0 0 5px rgba(128, 128, 128, 0.9);
}

.dropoff-warning {
  /* position: absolute; */
  z-index: 2;
  width: 100vw;
  max-width: 700px;
  text-align: center;
  margin-top: 10px;
  /* font-weight: 450; */
}

.dropoff-warning .notification {
  padding: 6px;
  margin-left: 10px;
  /* width: calc(100vw - 62px);
  max-width: calc(700px - 62px); */
  width: calc(100vw - 20px);
  max-width: calc(700px - 20px);
  /* margin: 0 50px; */
  background-color: rgba(255, 255, 255, 1);
  text-align: left;
  margin-bottom: 10px;
  box-shadow: 1px 1px 5px #bbb;
}

.dropoff-warning .tag {
  box-shadow: 1px 1px 5px #bbb;
}

.error-hint {
  font-size: 0.75em;
  background-color: yellow;
  padding: 2px 4px;
  border-radius: 4px;
}

.stops-wrapper {
  position: absolute;
  top: -2px;
  left: 0;
  z-index: 1202;
  /* width: calc(100vw - 110px); */
  /* width: calc(100vw - 6px); */
  width: 100vw;
  height: 100vh;
  background-color: #ddd;
  text-align: left;
  overflow: auto;
  pointer-events: auto;
  /* box-shadow: 0 0 3px #999; */
}

.stops-wrapper .list {
  font-size: 1.3em;
  border-radius: 0;
  font-weight: 500;
}

.list-item {
  border-bottom: initial;
  /* border-top: 1px solid rgb(235, 235, 235); */
  border-bottom: 1px solid rgb(235, 235, 235);
  padding: 12px 16px;
}

.list p {
  /* padding: 2px 8px; */
  font-weight: 400;
  font-size: 0.85em;
  color: grey;
}

/* .previously-used {
  margin-right: 6px;
  text-align: right;
  opacity: 0.6;
  font-weight: 450;
} */
.previously-used {
  padding: 2px;
  padding-top: 4px;
  padding-left: 15px;
  color: #999;
  font-weight: 450;
  background-color: whitesmoke;
}

.stop-name {
  /* display: inline-block; */
  /* max-width: calc(100vw - 60px); */
  white-space: nowrap;
  overflow: auto;
  text-overflow: ellipsis;
  vertical-align: bottom;
  font-weight: 400;
}

.stop-name.notes {
  background-color: whitesmoke;
  border-radius: 3px;
  border: 1px solid #eee;
  padding: 2px 4px;
  margin-top: 4px;
  /* color: #333; */
  /* box-shadow: 1px 1px 5px #bbb; */
}

.fa-history,
.fa-pencil-alt {
  color: grey;
  margin-right: 8px;
}

.places-btn {
  margin-right: -10px;
  margin-top: -5px;
  padding: 8px;
  /* color: grey; */
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 4px grey;
}

.map-btn {
  text-align: center;
  background-color: white;
  box-shadow: 0 0 4px black;
  color: black;
  /* var(--link); */
  text-shadow: 0 0 1px var(--info);
  border-radius: 3px;
  padding: 12px;
  font-size: 1.3em;
  background-image: url("~@/assets/map_button_background.png");
  background-size: cover;
  background-position: top;
  opacity: 0.5;
}

.editing-notes-info {
  background-color: whitesmoke;
  padding: 2px 4px;
  border-radius: 4px;
}

.textarea {
  pointer-events: initial;
  z-index: 99999;
}

.edit-notes-wrapper {
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(1px);
  position: absolute;
  bottom: 0;
  height: 100vh;
  width: 100vw;
  z-index: 1200;
}

.edit-notes-panel {
  text-align: left;
  position: absolute;
  bottom: 0;
  padding-bottom: 20px;
  background-color: whitesmoke;
  padding: 12px;
  z-index: 1200;
  width: 100vw;
  max-width: 700px;
  pointer-events: auto;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0 0 4px grey;
}

.cover-leaflet-links {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 150px;
  height: 24px;
}
</style>

<style lang="scss">
#shop-set-delivery-address-map {
  .leaflet-bottom .leaflet-control-zoom {
    margin-bottom: 100px;
  }

  .leaflet-popup-content {
    padding: 5px 5px;
    margin: 1px 0;
    color: rgb(23, 102, 193);
    font-size: 1.1em;
    // max-width: 130px;
  }

  // .leaflet-top {
  //   z-index: 0;
  // }
  .blue-dot {
    margin-left: 0px;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    background-color: #2693da;
    opacity: 0.9;
    border: 1px solid #feeaa4b5;
    animation: pulsate-3 3s ease-out infinite;
  }

  @keyframes pulsate-3 {
    0% {
      box-shadow: 0 0 0 1px cyan;
    }

    50% {
      box-shadow: 0 0 0 6px rgba(255, 217, 0, 0.5);
    }

    100% {
      box-shadow: 0 0 0 1px lightskyblue;
    }
  }

  @-webkit-keyframes pulsate-3 {
    0% {
      box-shadow: 0 0 0 1px cyan;
    }

    50% {
      box-shadow: 0 0 0 6px rgba(255, 217, 0, 0.5);
    }

    100% {
      box-shadow: 0 0 0 1px lightskyblue;
    }
  }
}

.pac-container {
  z-index: 1202;
}
</style>